import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Calendar as CalIcon,
  MapPin,
  ChevronRight,
  Clock,
  ArrowUpRight,
  Syringe,
  Sparkles,
  ShieldCheck,
  Stethoscope,
  Users,
  PlayCircle,
  Menu,
  Instagram,
  Phone,
  Coffee,
  UtensilsCrossed,
  Award,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { api } from "@/lib/api";
import {
  EVENT,
  THEMES,
  SESSIONS,
  FACULTY,
  COMMITTEE,
  TAKEAWAYS,
  FAQS,
  INTEREST_OPTIONS,
  QUALIFICATION_OPTIONS,
  SPECIALTY_OPTIONS,
  VIDEOS,
} from "@/lib/data";
import { GoldRule, Overline, Monogram, scrollToId } from "@/components/Primitives";
import IntroVideoModal from "@/components/IntroVideoModal";

/* ------------------------------ NAV ------------------------------ */
function NavBar() {
  const [open, setOpen] = useState(false);
  const nav = [
    { id: "why", label: "Why" },
    { id: "themes", label: "Themes" },
    { id: "program", label: "Program" },
    { id: "committee", label: "Committee" },
    { id: "faculty", label: "Faculty" },
    { id: "videos", label: "Videos" },
    { id: "venue", label: "Venue" },
    { id: "register", label: "Register" },
  ];
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-[#F5F2EA]/90 backdrop-blur-md border-b border-[#C5A059]/25"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
        <button
          onClick={() => scrollToId("top")}
          data-testid="logo-link"
          className="flex items-baseline gap-1.5 sm:gap-2 group min-w-0"
        >
          <span className="font-serif text-lg sm:text-xl text-[#0F5C46] tracking-tight">
            Aesthetic
          </span>
          <span className="font-serif text-lg sm:text-xl text-[#1A1A1A]">
            Symposium
          </span>
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[#A4863F] ml-0.5 sm:ml-1">
            2026
          </span>
        </button>
        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          {nav.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => scrollToId(n.id)}
              className="text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A] hover:text-[#0F5C46] transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scrollToId("register")}
            data-testid="nav-register-cta"
            className="hidden sm:inline-flex bg-[#059669] hover:bg-[#0F5C46] text-white text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 transition-colors"
          >
            Register
          </button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
                className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#C5A059]/40 text-[#1A1A1A] hover:bg-[#EBE7DD] transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="mobile-menu"
              className="bg-[#F5F2EA] border-l border-[#C5A059]/30 w-[85%] sm:w-[400px] p-0"
            >
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Site navigation menu
              </SheetDescription>
              <div className="px-6 pt-8 pb-6 border-b border-[#C5A059]/25">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#A4863F]">
                  Aesthetic Symposium
                </p>
                <p className="font-serif text-3xl text-[#1A1A1A] mt-1">2026</p>
                <p className="text-xs text-[#4A4A4A] mt-2">
                  5 July · Le Méridien, Amritsar
                </p>
              </div>
              <nav className="flex flex-col px-6 py-4">
                {nav.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`mobile-nav-${n.id}`}
                    onClick={() => {
                      setOpen(false);
                      setTimeout(() => scrollToId(n.id), 150);
                    }}
                    className="text-left py-3 border-b border-[#C5A059]/15 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] hover:text-[#0F5C46] transition-colors"
                  >
                    {n.label}
                  </button>
                ))}
              </nav>
              <div className="px-6 pt-2 pb-6">
                <button
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollToId("register"), 150);
                  }}
                  data-testid="mobile-register-btn"
                  className="w-full bg-[#059669] hover:bg-[#0F5C46] text-white text-xs uppercase tracking-[0.22em] px-5 py-3.5 transition-colors"
                >
                  Register Now
                </button>
                <div className="mt-6 space-y-3 text-sm text-[#4A4A4A]">
                  <a
                    href={`tel:${EVENT.phoneRaw}`}
                    className="flex items-center gap-2 hover:text-[#0F5C46]"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {EVENT.phone}
                  </a>
                  <a
                    href={EVENT.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 hover:text-[#0F5C46]"
                  >
                    <Instagram className="w-3.5 h-3.5" />
                    {EVENT.instagramHandle}
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

/* ------------------------------ HERO ------------------------------ */
function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden border-b border-[#C5A059]/20"
    >
      {/* decorative subtle radial */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 30%, rgba(15,92,70,0.06) 0%, transparent 60%), radial-gradient(45% 40% at 10% 80%, rgba(197,160,89,0.10) 0%, transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-14 sm:pt-20 md:pt-28 pb-14 sm:pb-20 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="lg:col-span-8"
          >
            <Overline testId="hero-eyebrow">
              An educational initiative by Eden Skin Clinic · CME
            </Overline>
            <h1
              data-testid="hero-title"
              className="mt-5 font-serif text-[2.75rem] sm:text-6xl lg:text-[6.5rem] leading-[1.02] tracking-tight text-[#1A1A1A]"
            >
              Aesthetic
              <br />
              <span className="italic text-[#0F5C46]">Symposium</span> 2026
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-5 sm:mt-6 font-serif text-xl sm:text-2xl md:text-3xl text-[#1A1A1A]/85"
            >
              Botox · Fillers · Threads
            </p>
            <p
              data-testid="hero-tagline"
              className="mt-3 max-w-2xl text-sm sm:text-base md:text-lg text-[#4A4A4A] leading-relaxed"
            >
              Practical injectables. Clinical judgement. Better patient
              outcomes.
            </p>
            <p
              data-testid="hero-tagline-2"
              className="mt-3 max-w-2xl text-sm sm:text-base md:text-lg text-[#4A4A4A] leading-relaxed"
            >
              A focused CME for dermatologists and plastic surgeons — moving
              beyond fixed protocols into real-world decision-making.
            </p>

            <div className="mt-7 sm:mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
              <button
                onClick={() => scrollToId("register")}
                data-testid="hero-register-btn"
                className="group inline-flex items-center gap-3 bg-[#059669] text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em] font-medium hover:bg-[#0F5C46] transition-colors"
              >
                Register Now
                <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                onClick={() => scrollToId("program")}
                data-testid="hero-program-btn"
                className="inline-flex items-center gap-3 border border-[#C5A059] text-[#1A1A1A] px-7 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-[#C5A059] hover:text-white transition-colors"
              >
                View Scientific Program
              </button>
            </div>
            <p
              className="mt-4 text-xs text-[#4A4A4A]"
              data-testid="hero-seats-note"
            >
              Limited seats. Registration is complimentary but mandatory.
            </p>
          </motion.div>

          {/* Right rail: date / venue card */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: "easeOut" }}
            className="lg:col-span-4"
            data-testid="hero-info-card"
          >
            <div className="bg-white border border-[#C5A059]/40 p-8 md:p-10">
              <div className="flex items-start gap-4 pb-6 border-b border-[#C5A059]/25">
                <CalIcon className="w-5 h-5 text-[#0F5C46] mt-1" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#A4863F]">
                    Date
                  </p>
                  <p className="font-serif text-2xl text-[#1A1A1A] mt-1">
                    5 July 2026
                  </p>
                  <p className="text-xs text-[#4A4A4A] mt-1">Sunday · 09:00 – 17:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4 pt-6">
                <MapPin className="w-5 h-5 text-[#0F5C46] mt-1" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#A4863F]">
                    Venue
                  </p>
                  <p className="font-serif text-2xl text-[#1A1A1A] mt-1">
                    Le Méridien
                  </p>
                  <p className="text-sm text-[#4A4A4A]">Amritsar, Punjab</p>
                  <a
                    href={EVENT.mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="hero-directions-link"
                    className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#0F5C46] hover:underline"
                  >
                    Get directions <ArrowUpRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-[#C5A059]/25">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#A4863F]">
                  PMC Accreditation
                </p>
                <p className="text-sm text-[#B91C1C] font-semibold mt-1">
                  4 CME points accredited by PMC.
                </p>
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ WHY ------------------------------ */
function WhySection() {
  return (
    <section id="why" className="section-pad border-b border-[#C5A059]/20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline>Why this Symposium</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              Evidence meets <span className="italic text-[#0F5C46]">clinical judgement.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            <p>
              Aesthetic Symposium 2026 has been designed with one clear
              objective: to help doctors understand not only the basics of
              injectables, but also the practical decision-making that experts
              use in real practice.
            </p>
            <p>
              Aesthetics is not about the same number of Botox units or filler
              syringes for every patient. It is about assessing the face,
              understanding anatomy, identifying what the patient needs,
              knowing what to avoid — and delivering natural, individualised,
              refined results.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------- WHAT MAKES THIS DIFFERENT --------------------- */
function DifferentSection() {
  const points = [
    "How experts assess the face before planning injectables",
    "How treatment decisions are made for Botox, Fillers and Threads",
    "How to move from fixed protocols to individualised treatment planning",
    "How to understand complications and manage them confidently",
    "How to apply clinical pearls directly in everyday practice",
    "How to ask questions freely and learn from expert experience",
  ];
  return (
    <section className="section-pad bg-[#EBE7DD]/40 border-b border-[#C5A059]/20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Overline>What makes Aesthetic Symposium different</Overline>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] max-w-3xl leading-tight">
          Not a lecture series. A practical learning platform.
        </h2>
        <p className="mt-5 max-w-2xl text-[#4A4A4A] text-base md:text-lg leading-relaxed">
          See how faculty think, plan, inject and manage real cases. Take home
          decisions, not just slides.
        </p>

        <div className="mt-12 grid md:grid-cols-2 gap-x-12 gap-y-5">
          {points.map((p, i) => (
            <div
              key={i}
              data-testid={`different-point-${i}`}
              className="flex items-start gap-4 border-t border-[#C5A059]/25 pt-4"
            >
              <span className="font-serif text-2xl text-[#C5A059] leading-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="text-[#1A1A1A] text-base leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------- WHO SHOULD ATTEND ------------------------- */
function AudienceSection() {
  const items = [
    { icon: Stethoscope, text: "Dermatologists beginning their aesthetic journey" },
    { icon: Syringe, text: "Dermatologists already performing injectables" },
    { icon: Sparkles, text: "Plastic surgeons interested in aesthetic injectables" },
    { icon: ShieldCheck, text: "Plastic surgeons refining their injectable practice" },
    { icon: Users, text: "Young doctors building confidence in Botox, fillers, threads" },
    { icon: Stethoscope, text: "Practitioners learning from expert-led decision-making" },
  ];
  return (
    <section className="section-pad border-b border-[#C5A059]/20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <Overline>Who should attend</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              Built for the doctor <span className="italic text-[#0F5C46]">at every stage.</span>
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            Whether you are starting out or already experienced, you will leave
            with practical points that improve confidence, technique and
            patient outcomes.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#C5A059]/20">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <div
                key={i}
                data-testid={`audience-${i}`}
                className="bg-[#F5F2EA] p-8 hover:bg-white transition-colors"
              >
                <Icon className="w-5 h-5 text-[#0F5C46]" />
                <p className="mt-5 text-[#1A1A1A] text-base leading-relaxed">
                  {it.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- CORE THEMES --------------------------- */
function ThemesSection() {
  return (
    <section id="themes" className="section-pad bg-[#FBF8F1] border-b border-[#C5A059]/20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Overline>Core learning themes</Overline>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] max-w-3xl leading-tight">
          A narrative arc — from anatomy to outcome.
        </h2>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {THEMES.map((t) => (
            <article
              key={t.n}
              data-testid={`theme-${t.n}`}
              className="bg-white border border-[#C5A059]/25 p-7 hover:-translate-y-1 transition-transform duration-300"
            >
              <p className="font-serif text-3xl text-[#C5A059]">{t.n}</p>
              <h3 className="mt-3 font-serif text-2xl text-[#1A1A1A] leading-tight">
                {t.title}
              </h3>
              <GoldRule className="my-4" />
              <p className="text-sm text-[#4A4A4A] leading-relaxed">{t.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- PROGRAM --------------------------- */
function ProgramSection() {
  return (
    <section id="program" className="section-pad border-b border-[#C5A059]/20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Scientific Program</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              The day, hour by hour.
            </h2>
          </div>
          <p className="max-w-md text-sm text-[#4A4A4A]">
            Program schedule is subject to change.{" "}
            <span className="text-[#B91C1C] font-semibold">4 CME points accredited by PMC.</span>
            {" "}Registration is{" "}
            <span className="text-[#0F5C46] font-medium">
              complimentary but mandatory.
            </span>
          </p>
        </div>

        <div className="mt-10 space-y-px bg-[#C5A059]/15">
          {SESSIONS.map((s, i) => {
            const isBreak = s.kind === "break";
            const isCeremony = s.kind === "ceremony";
            const BreakIcon = /lunch/i.test(s.title)
              ? UtensilsCrossed
              : Coffee;

            if (isBreak || isCeremony) {
              return (
                <article
                  key={i}
                  data-testid={`session-${i}`}
                  className={`px-5 sm:px-6 md:px-8 py-5 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-center transition-colors ${
                    isCeremony
                      ? "bg-[#0F5C46]/[0.06] hover:bg-[#0F5C46]/10"
                      : "bg-[#EBE7DD]/60 hover:bg-[#EBE7DD]"
                  }`}
                >
                  <div className="md:col-span-3 flex items-center gap-2 text-[#0F5C46]">
                    <Clock className="w-3.5 h-3.5 shrink-0" />
                    <p className="text-xs uppercase tracking-[0.2em]">{s.time}</p>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#A4863F] ml-2">
                      {s.duration}
                    </span>
                  </div>
                  <div className="md:col-span-6 flex items-center gap-3">
                    {isCeremony ? (
                      <Award className="w-4 h-4 text-[#C5A059] shrink-0" />
                    ) : (
                      <BreakIcon className="w-4 h-4 text-[#A4863F] shrink-0" />
                    )}
                    <p
                      className={`font-serif leading-snug ${
                        isCeremony
                          ? "text-2xl text-[#0F5C46]"
                          : "text-xl text-[#1A1A1A]"
                      }`}
                    >
                      {s.title}
                    </p>
                  </div>
                  <div className="md:col-span-3">
                    {isCeremony && s.speakers && s.speakers.length > 0 && (
                      <>
                        <p className="text-[10px] uppercase tracking-[0.22em] text-[#A4863F]">
                          {s.speakers.length > 1 ? "Speakers" : "Speaker"}
                        </p>
                        <ul className="mt-2 space-y-1 text-sm text-[#1A1A1A]">
                          {s.speakers.map((sp, k) => (
                            <li key={k}>{sp}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </article>
              );
            }

            return (
              <article
                key={i}
                data-testid={`session-${i}`}
                className="bg-[#F5F2EA] hover:bg-white transition-colors px-5 sm:px-6 md:px-8 py-6 md:py-7 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8"
              >
                <div className="md:col-span-3">
                  <div className="flex items-center gap-2 text-[#0F5C46]">
                    <Clock className="w-3.5 h-3.5" />
                    <p className="text-xs uppercase tracking-[0.2em]">{s.time}</p>
                  </div>
                  <p className="mt-1.5 md:mt-2 text-[10px] uppercase tracking-[0.25em] text-[#A4863F]">
                    {s.duration}
                  </p>
                </div>
                <div className="md:col-span-6">
                  <h4 className="font-serif text-xl md:text-2xl text-[#1A1A1A] leading-snug">
                    {s.title}
                  </h4>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#4A4A4A]">
                    {s.items.map((it, j) => (
                      <li key={j} className="leading-relaxed">
                        · {it}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="md:col-span-3">
                  {s.panelists && s.panelists.length > 0 && (
                    <>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#A4863F]">
                        Panelists
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-[#1A1A1A]">
                        {s.panelists.map((p, k) => (
                          <li key={k}>{p}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {s.speakers.length > 0 && (
                    <div
                      className={
                        s.panelists && s.panelists.length > 0 ? "mt-4" : ""
                      }
                    >
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#A4863F]">
                        {s.title.toLowerCase().includes("panel")
                          ? "Moderators"
                          : "Speakers"}
                      </p>
                      <ul className="mt-2 space-y-1 text-sm text-[#1A1A1A]">
                        {s.speakers.map((sp, k) => (
                          <li key={k}>{sp}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        <p
          className="mt-6 text-sm text-[#0F5C46]"
          data-testid="agenda-comp-note"
        >
          Registration is complimentary but mandatory.{" "}
          <span className="text-[#B91C1C] font-semibold">4 CME points accredited by PMC.</span>
        </p>
      </div>
    </section>
  );
}

/* --------------------------- COMMITTEE --------------------------- */
function CommitteeSection() {
  return (
    <section
      id="committee"
      className="section-pad border-b border-[#C5A059]/20 bg-[#FBF8F1]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <Overline>Organising Committee</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              The team behind the <span className="italic text-[#0F5C46]">symposium.</span>
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            A core group of dermatologists and plastic surgeons curating the
            scientific content, faculty and clinical narrative for the day.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#C5A059]/20">
          {COMMITTEE.map((c) => (
            <div
              key={c.name}
              data-testid={`committee-${c.initials}`}
              className="bg-[#F5F2EA] hover:bg-white transition-colors p-8 flex flex-col items-center text-center"
            >
              <Monogram
                initials={c.initials}
                image={c.image}
                name={c.name}
                imagePosition={c.imagePos}
                size="lg"
              />
              <h4 className="mt-5 font-serif text-xl text-[#1A1A1A]">
                {c.name}
              </h4>
              <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-[#A4863F] leading-relaxed">
                {c.role}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- FACULTY --------------------------- */
function FacultySection() {
  return (
    <section id="faculty" className="section-pad border-b border-[#C5A059]/20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-5">
            <Overline>Faculty</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              Learn from the <span className="italic text-[#0F5C46]">best minds in aesthetics.</span>
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            A focused panel of senior dermatologists and plastic surgeons —
            each bringing years of practical injectable experience to the
            symposium.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-[#C5A059]/20">
          {FACULTY.map((f) => (
            <div
              key={f.name}
              data-testid={`faculty-${f.initials}`}
              className="bg-[#F5F2EA] hover:bg-white transition-colors p-8 flex flex-col items-center text-center"
            >
              <Monogram
                initials={f.initials}
                image={f.image}
                name={f.name}
                imagePosition={f.imagePos}
                testId={`monogram-${f.initials}`}
              />
              <h4 className="mt-5 font-serif text-xl text-[#1A1A1A]">
                {f.name}
              </h4>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- VIDEOS --------------------------- */
function instagramEmbed(url) {
  if (!url) return null;
  const m = url.match(/instagram\.com\/(reel|p|tv)\/([\w-]+)/);
  return m ? `https://www.instagram.com/${m[1]}/${m[2]}/embed` : null;
}

function VideoCard({ v }) {
  const isYoutube = v.videoUrl && /youtu\.?be/.test(v.videoUrl);
  const isVimeo = v.videoUrl && /vimeo/.test(v.videoUrl);
  const embedUrl = (() => {
    if (!v.videoUrl) return null;
    if (isYoutube) {
      const id =
        v.videoUrl.match(/(?:v=|youtu\.be\/|\/embed\/)([\w-]{6,})/)?.[1];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (isVimeo) {
      const id = v.videoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
    return v.videoUrl;
  })();
  const igEmbedUrl = instagramEmbed(v.instagramUrl);
  const isPortrait = !embedUrl && !!igEmbedUrl;

  return (
    <article
      data-testid={`video-${v.initials}`}
      className={`bg-white border border-[#C5A059]/30 group ${
        isPortrait ? "sm:row-span-2" : ""
      }`}
    >
      <div
        className={`relative ${
          isPortrait ? "aspect-[9/16]" : "aspect-[4/3]"
        } overflow-hidden bg-[#FBF8F1]`}
      >
        {embedUrl ? (
          isYoutube || isVimeo ? (
            <iframe
              src={embedUrl}
              title={v.name}
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={embedUrl}
              className="absolute inset-0 w-full h-full object-cover"
              controls
              preload="metadata"
            />
          )
        ) : igEmbedUrl ? (
          <iframe
            src={igEmbedUrl}
            title={`${v.name} · Instagram reel`}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            scrolling="no"
            frameBorder="0"
            data-testid={`video-instagram-${v.initials}`}
          />
        ) : (
          <div className="absolute inset-0">
            {v.image ? (
              <>
                <img
                  src={v.image}
                  alt={v.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: v.imagePos || "50% 18%" }}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B4636]/70 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-3 flex justify-center">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white">
                    <PlayCircle className="w-3.5 h-3.5" /> Video coming soon
                  </span>
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <Monogram initials={v.initials} size="md" />
                <div className="mt-5 inline-flex items-center gap-2 text-[#0F5C46]">
                  <PlayCircle className="w-5 h-5" />
                  <span className="text-[10px] uppercase tracking-[0.25em]">
                    Video coming soon
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        <span className="absolute top-3 left-3 z-10 bg-[#0B4636] text-[#F5F2EA] text-[9px] uppercase tracking-[0.22em] px-2 py-1">
          {v.group}
        </span>
      </div>
      <div className="p-5">
        <p className="font-serif text-lg text-[#1A1A1A] leading-tight">
          {v.name}
        </p>
        {v.group !== "Faculty" && (
          <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#A4863F]">
            {v.role}
          </p>
        )}
      </div>
    </article>
  );
}

function VideosSection() {
  return (
    <section
      id="videos"
      className="section-pad border-b border-[#C5A059]/20 bg-[#EBE7DD]/40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline>Voices from the Faculty</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              A word from the committee &amp;{" "}
              <span className="italic text-[#0F5C46]">faculty.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#4A4A4A] text-base leading-relaxed">
            Short messages from the people behind Aesthetic Symposium 2026 —
            why they teach, what to expect, and what they hope you take home.
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-start grid-flow-dense">
          {VIDEOS.map((v) => (
            <VideoCard key={v.name} v={v} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- VENUE --------------------------- */
function VenueSection() {
  return (
    <section
      id="venue"
      className="section-pad border-b border-[#C5A059]/20 bg-[#FBF8F1]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 bg-white border border-[#C5A059]/30 p-10">
            <Overline>Venue</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              Le Méridien,{" "}
              <span className="italic text-[#0F5C46]">Amritsar</span>
            </h2>
            <p className="mt-5 text-[#4A4A4A] text-base leading-relaxed max-w-xl">
              Join us in the vibrant city of Amritsar for a day of practical
              learning, academic exchange and meaningful professional
              networking.
            </p>
            <a
              href={EVENT.mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="venue-directions-link"
              className="mt-8 inline-flex items-center gap-3 border border-[#C5A059] text-[#1A1A1A] px-6 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-white transition-colors"
            >
              Get Directions
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="lg:col-span-5">
            <div
              className="aspect-[4/3] w-full bg-[#FBF8F1] border border-[#C5A059]/30 relative overflow-hidden"
              data-testid="venue-visual"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center px-6">
                  <MapPin className="w-8 h-8 text-[#0F5C46] mx-auto" />
                  <p className="mt-4 font-serif text-3xl text-[#1A1A1A]">
                    Amritsar
                  </p>
                  <p className="mt-2 text-sm text-[#4A4A4A]">
                    Le Méridien Amritsar · Punjab, India
                  </p>
                  <p className="mt-6 text-[10px] uppercase tracking-[0.25em] text-[#A4863F]">
                    5 July 2026 · 09:00 – 17:00
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------- TAKE HOME + MESSAGE ------------------------- */
function TakeHomeSection() {
  return (
    <section className="section-pad bg-[#0B4636] text-[#F5F2EA] border-b border-[#0F5C46]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#C5A059]">
            What you will take home
          </p>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
            Think better. Decide better. Inject safer.
          </h2>
          <p className="mt-6 text-[#F5F2EA]/80 leading-relaxed">
            The aim is not only to teach procedures — it is to help doctors
            deliver more natural patient outcomes, informed by experience and
            anatomy.
          </p>
        </div>
        <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-4">
          {TAKEAWAYS.map((t, i) => (
            <li
              key={i}
              data-testid={`takeaway-${i}`}
              className="border-t border-[#C5A059]/30 pt-4 flex items-start gap-3"
            >
              <span className="font-serif text-xl text-[#C5A059] leading-none">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="text-[#F5F2EA]/90">{t}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-20">
        <div className="border-t border-[#C5A059]/30 pt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#C5A059]">
              A note for young dermatologists
            </p>
          </div>
          <div className="lg:col-span-7">
            <blockquote className="font-serif text-3xl md:text-4xl leading-tight">
              “Inject without fear — but never inject without knowledge.”
            </blockquote>
            <p className="mt-6 text-[#F5F2EA]/80 leading-relaxed max-w-2xl">
              Confidence in aesthetics does not come from ignoring
              complications. It comes from understanding anatomy, learning
              protocols, knowing risks — and being prepared to manage
              complications if they occur.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- REGISTRATION --------------------------- */
function RegistrationSection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    qualification: "",
    specialty: "",
    mci_number: "",
    clinic_or_hospital: "",
    city: "",
    mobile: "",
    email: "",
    currently_injecting: "",
    areas_of_interest: [],
    social_profile: "",
  });

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleInterest(opt) {
    setForm((f) => {
      const has = f.areas_of_interest.includes(opt);
      let next;
      if (opt === "All of the above") {
        next = has ? [] : [...INTEREST_OPTIONS];
      } else {
        next = has
          ? f.areas_of_interest.filter((x) => x !== opt)
          : [...f.areas_of_interest.filter((x) => x !== "All of the above"), opt];
      }
      return { ...f, areas_of_interest: next };
    });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.qualification) {
      toast.error("Please select your qualification");
      return;
    }
    if (!form.specialty) {
      toast.error("Please select your specialty");
      return;
    }
    if (!form.currently_injecting) {
      toast.error("Please tell us if you are currently performing injectables");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/registrations", form);
      setSubmitted(true);
      toast.success("Registration received");
      window.scrollTo({ top: document.getElementById("register").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not submit. Please check the form and try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <section
        id="register"
        data-testid="registration-success"
        className="section-pad border-b border-[#C5A059]/20 bg-[#FBF8F1]"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <Overline>Thank you</Overline>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
            Your seat is being <span className="italic text-[#0F5C46]">reserved.</span>
          </h2>
          <GoldRule className="my-8 max-w-xs mx-auto" />
          <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            Thank you for registering for Aesthetic Symposium 2026. Our team
            will verify your details and share confirmation on your registered
            WhatsApp number and email.
          </p>
          <p className="mt-6 text-sm text-[#0F5C46]">
            Registration is complimentary but mandatory. Please keep an eye on
            your inbox.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="register"
      data-testid="registration-section"
      className="section-pad border-b border-[#C5A059]/20"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Overline>Registration</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl text-[#1A1A1A] leading-tight">
              Reserve your seat at <span className="italic text-[#0F5C46]">Aesthetic Symposium 2026</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-sm text-[#4A4A4A] leading-relaxed">
            <p>
              <span className="text-[#0F5C46] font-medium">
                Complimentary but mandatory.
              </span>{" "}
              Open to dermatologists and plastic surgeons only. Limited seats.
            </p>
            <p className="mt-3">5 July 2026 · Le Méridien Amritsar</p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="registration-form"
          className="mt-12 bg-white border border-[#C5A059]/30 p-6 md:p-12 grid md:grid-cols-2 gap-x-8 gap-y-6"
        >
          <Field label="Full Name" required>
            <Input
              data-testid="reg-full-name"
              required
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>
          <Field label="Qualification" required>
            <Select
              value={form.qualification}
              onValueChange={(v) => update("qualification", v)}
            >
              <SelectTrigger
                data-testid="reg-qualification"
                className="rounded-none border-[#C5A059]/40 focus:ring-[#0F5C46] focus:ring-1"
              >
                <SelectValue placeholder="Select your qualification" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {QUALIFICATION_OPTIONS.map((q) => (
                  <SelectItem key={q} value={q}>
                    {q}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Specialty" required>
            <Select
              value={form.specialty}
              onValueChange={(v) => update("specialty", v)}
            >
              <SelectTrigger
                data-testid="reg-specialty"
                className="rounded-none border-[#C5A059]/40 focus:ring-[#0F5C46] focus:ring-1"
              >
                <SelectValue placeholder="Select your specialty" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {SPECIALTY_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Medical Council Registration No." required>
            <Input
              data-testid="reg-mci"
              required
              value={form.mci_number}
              onChange={(e) => update("mci_number", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>
          <Field label="Clinic / Hospital Name" required wide>
            <Input
              data-testid="reg-clinic"
              required
              value={form.clinic_or_hospital}
              onChange={(e) => update("clinic_or_hospital", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>
          <Field label="City" required>
            <Input
              data-testid="reg-city"
              required
              value={form.city}
              onChange={(e) => update("city", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>
          <Field label="Email" required>
            <Input
              data-testid="reg-email"
              required
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>
          <Field label="Mobile Number" required>
            <Input
              data-testid="reg-mobile"
              required
              type="tel"
              value={form.mobile}
              onChange={(e) => update("mobile", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>

          <Field label="LinkedIn or Instagram Profile (optional)" wide>
            <Input
              data-testid="reg-social-profile"
              type="url"
              placeholder="https://www.linkedin.com/in/your-handle or https://instagram.com/your-handle"
              value={form.social_profile}
              onChange={(e) => update("social_profile", e.target.value)}
              className="rounded-none border-[#C5A059]/40 focus-visible:ring-[#0F5C46] focus-visible:ring-1"
            />
          </Field>

          <Field label="Are you currently performing injectables?" required>
            <Select
              value={form.currently_injecting}
              onValueChange={(v) => update("currently_injecting", v)}
            >
              <SelectTrigger
                data-testid="reg-injecting"
                className="rounded-none border-[#C5A059]/40 focus:ring-[#0F5C46] focus:ring-1"
              >
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="planning">Planning to start</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <div className="md:col-span-2 pt-2">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#4A4A4A]">
              Areas of Interest
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {INTEREST_OPTIONS.map((opt) => {
                const active = form.areas_of_interest.includes(opt);
                return (
                  <button
                    type="button"
                    key={opt}
                    data-testid={`reg-interest-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggleInterest(opt)}
                    className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border transition-colors ${
                      active
                        ? "bg-[#0F5C46] border-[#0F5C46] text-white"
                        : "bg-transparent border-[#C5A059]/50 text-[#1A1A1A] hover:bg-[#C5A059]/10"
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2 pt-6 border-t border-[#C5A059]/25 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-[#4A4A4A] max-w-md">
              By submitting, you agree to be contacted on your mobile number
              and email regarding confirmation and event updates.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="reg-submit-btn"
              className="bg-[#059669] hover:bg-[#0F5C46] text-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit Registration"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, wide, children }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-[#4A4A4A]">
        {label}
        {required && <span className="text-[#0F5C46] ml-1">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/* ------------------------------ FAQ ------------------------------ */
function FAQSection() {
  return (
    <section className="section-pad border-b border-[#C5A059]/20 bg-[#FBF8F1]">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <Overline>FAQ</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl text-[#1A1A1A] leading-tight">
              Common questions, <span className="italic text-[#0F5C46]">clearly answered.</span>
            </h2>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`q-${i}`}
                  className="border-b border-[#C5A059]/25"
                >
                  <AccordionTrigger
                    data-testid={`faq-q-${i}`}
                    className="font-serif text-xl md:text-2xl text-[#1A1A1A] hover:no-underline text-left py-5"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-a-${i}`}
                    className="text-[#4A4A4A] text-base leading-relaxed pb-5"
                  >
                    <span dangerouslySetInnerHTML={{ __html: f.a }} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ FINAL CTA ------------------------------ */
function FinalCTA() {
  return (
    <section className="section-pad border-b border-[#C5A059]/20">
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <Overline>Aesthetic Symposium 2026</Overline>
        <h2 className="mt-5 font-serif text-4xl md:text-6xl text-[#1A1A1A] leading-[1.05]">
          Learn from the best minds in aesthetics. Take home points you can
          apply <span className="italic text-[#0F5C46]">on Monday morning.</span>
        </h2>
        <p className="mt-6 text-[#4A4A4A] text-base md:text-lg max-w-2xl mx-auto">
          Expert sessions, live video demonstrations, open Q&A and real-world
          clinical discussions — in one focused CME.
        </p>
        <button
          onClick={() => scrollToId("register")}
          data-testid="final-cta-btn"
          className="mt-10 inline-flex items-center gap-3 bg-[#059669] hover:bg-[#0F5C46] text-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] transition-colors"
        >
          Register Now
          <ChevronRight className="w-4 h-4" />
        </button>
        <p className="mt-4 text-xs text-[#4A4A4A]">
          Complimentary but mandatory · Limited seats
        </p>
      </div>
    </section>
  );
}

/* ------------------------------ FOOTER ------------------------------ */
function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0B4636] text-[#F5F2EA] py-14 sm:py-16 px-5 sm:px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <p className="font-serif text-2xl sm:text-3xl">Aesthetic Symposium 2026</p>
          <p className="mt-2 text-sm text-[#F5F2EA]/70">
            Botox · Fillers · Threads
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.22em] text-[#C5A059]">
            An educational initiative by
          </p>
          <p className="mt-1 text-base">Eden Skin Clinic</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#C5A059]">
            Event
          </p>
          <p className="mt-2 text-sm text-[#F5F2EA]/80">5 July 2026</p>
          <p className="text-sm text-[#F5F2EA]/80">09:00 – 17:00</p>
          <p className="text-sm text-[#F5F2EA]/80 mt-1">
            Le Méridien Amritsar
          </p>
          <a
            href={EVENT.mapsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-directions-link"
            className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-white"
          >
            Get Directions <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>

        <div data-testid="footer-secretariat">
          <p className="text-xs uppercase tracking-[0.22em] text-[#C5A059]">
            Conference Secretariat
          </p>
          <p className="mt-2 text-sm text-[#F5F2EA]/90">
            {EVENT.secretariat.name}
          </p>
          <p className="text-sm text-[#F5F2EA]/80">
            {EVENT.secretariat.address}
          </p>
          <a
            href={EVENT.secretariat.mapsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="secretariat-directions-link"
            className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-white"
          >
            View on map <ArrowUpRight className="w-3 h-3" />
          </a>
        </div>

        <div data-testid="footer-contact">
          <p className="text-xs uppercase tracking-[0.22em] text-[#C5A059]">
            Contact
          </p>
          <a
            href={`tel:${EVENT.phoneRaw}`}
            data-testid="footer-phone"
            className="mt-3 flex items-center gap-2 text-sm text-[#F5F2EA]/90 hover:text-[#C5A059] transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            {EVENT.phone}
          </a>
          <a
            href={EVENT.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-instagram"
            className="mt-2 flex items-center gap-2 text-sm text-[#F5F2EA]/90 hover:text-[#C5A059] transition-colors"
          >
            <Instagram className="w-3.5 h-3.5" />
            {EVENT.instagramHandle}
          </a>
          <p className="mt-5 text-xs text-[#F5F2EA]/60 max-w-xs leading-relaxed">
            Program schedule is subject to change.{" "}
            <span className="text-[#F87171] font-semibold">4 CME points accredited by PMC.</span>
            {" "}Registration is complimentary but mandatory.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-12 pt-6 border-t border-[#C5A059]/25 flex flex-wrap items-center justify-between gap-4 text-[11px] uppercase tracking-[0.22em] text-[#F5F2EA]/60">
        <span>© 2026 Eden Skin Clinic · aestheticSymposium.in</span>
        <a
          href="/admin/login"
          data-testid="footer-admin-link"
          className="hover:text-[#C5A059]"
        >
          Administration
        </a>
      </div>
    </footer>
  );
}

/* ------------------------------ PAGE ------------------------------ */
export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#F5F2EA] text-[#1A1A1A]">
      <IntroVideoModal />
      <NavBar />
      <Hero />
      <WhySection />
      <DifferentSection />
      <AudienceSection />
      <ThemesSection />
      <ProgramSection />
      <CommitteeSection />
      <FacultySection />
      <VideosSection />
      <VenueSection />
      <TakeHomeSection />
      <RegistrationSection />
      <FAQSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}
